/**
 * The two-step start's state for one organisation: which steps the viewer
 * sees, which are done, which they skipped, and whether the flow still has
 * something to offer them. The facts are the ones the setup checklist
 * uses (a code source exists; ``useCanRunSessions``: a usable agent login
 * or a custom model of the organisation), so the two can't disagree.
 */
import { useCodeSources, useMe } from "@/api/hooks";
import type { Me } from "@/api/types";

import {
  buildOnboardingSteps,
  flowPending,
  type FlowSteps,
} from "./onboardingFlow";
import { useFlowState } from "./onboardingFlowState";
import { useCanRunSessions } from "./useCanRunSessions";

/** Whether the server's ``require_org_manager`` lets the viewer add code
 *  sources to ``slug``: an admin or manager of the organisation, or a
 *  platform admin (global admin or superadmin), who acts as the admin of
 *  every organisation. */
export function managesCodeSources(me: Me | undefined, slug: string | null): boolean {
  const scope = me?.admin_scope;
  if (!slug || !scope) return false;
  if (scope.is_global_admin || scope.is_superadmin) return true;
  if ((scope.org_admin_slugs ?? []).includes(slug)) return true;
  return (scope.org_manager_slugs ?? []).includes(slug);
}

export type OnboardingFlow = {
  slug: string | null;
  steps: FlowSteps;
  /** A visible step is neither done nor skipped, and the flow isn't finished. */
  pending: boolean;
  finished: boolean;
  /** Every query behind the steps has settled. */
  ready: boolean;
  /** The viewer is a member of the organisation (not a superadmin looking in). */
  isMember: boolean;
  canManageSources: boolean;
  /** How the agent step is met, for its wording: a login of the viewer's
   *  own or central billing, only a custom model, no runtime to link at
   *  all (the step can't be done, so it reads as done), or not yet. */
  agentAccess: "builtin" | "custom_model" | "none_registered" | null;
};

export function useOnboardingFlow(
  slugArg?: string | null,
  options: { createdNow?: boolean } = {},
): OnboardingFlow {
  const me = useMe();
  const slug = slugArg ?? me.data?.admin_scope?.active_org_slug ?? null;
  // The creator of an organisation made in this visit is its admin, before
  // /auth/me has caught up with the new membership.
  const canManageSources = Boolean(options.createdNow) || managesCodeSources(me.data, slug);
  const sources = useCodeSources(slug ?? "");
  const access = useCanRunSessions(slug);
  const state = useFlowState(slug);

  const steps = buildOnboardingSteps({
    canManageSources,
    hasSource: (sources.data?.length ?? 0) > 0,
    // With no runtime registered nobody can link anything: the step must
    // not keep the flow open (and opening by itself) for ever.
    hasCredential: access.any || access.noneRegistered,
    skipped: state.skipped,
    finished: state.finished,
  });
  const isMember =
    Boolean(options.createdNow) ||
    (me.data?.admin_scope?.org_member_slugs ?? []).includes(slug ?? "");
  const ready = !me.isLoading && !sources.isLoading && access.ready;
  const agentAccess = access.builtin
    ? "builtin"
    : access.any
      ? "custom_model"
      : access.noneRegistered
        ? "none_registered"
        : null;

  return {
    slug,
    steps,
    pending: flowPending(steps, state.finished),
    finished: state.finished,
    ready,
    isMember,
    canManageSources,
    agentAccess,
  };
}
